import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import {
  loginUser,
  clearError,
  setError,
  fetchTenantInfo,
  ssoLogin,
  googleSsoLogin
} from '../../store/authSlice';
import { getMsalInstance, getMsalPopupRedirectUri } from '../../services/msalInstance';
import { REGEX } from '../../shared/regex';
import { COLORS } from '../../shared/colors';
import { ROUTES } from '../../shared/appConstants';
import { dividerLine } from '../../shared/globalSX';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import './LoginBackground.css';

const getSSOErrorMessage = (error, fallbackMessage, cancelledMessage) => {
  const errorMessage = error?.message || '';

  if (
    error?.errorCode === 'user_cancelled' ||
    /user cancelled the flow/i.test(errorMessage)
  ) {
    return cancelledMessage;
  }

  return errorMessage || fallbackMessage;
};

const ssoIconButtonSx = {
  width: 52,
  height: 52,
  borderRadius: '2px',
  border: '0.44px solid #082B72',
  background: '#E8F0FF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'none',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover:not(:disabled)': {
    boxShadow: 'none',
    transform: 'translateY(-1px)'
  }
};

const GoogleIconButton = ({ onSuccess, onError, onCancel, loading, title }) => {
  const startGoogleLogin = useGoogleLogin({
    onSuccess,
    onError,
    onNonOAuthError: onCancel,
    scope: 'openid email profile',
    prompt: 'select_account'
  });

  return (
    <Box
      component="button"
      type="button"
      title={title}
      disabled={loading}
      onClick={startGoogleLogin}
      sx={{
        position: 'relative',
        ...ssoIconButtonSx,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.5 : 1,
        overflow: 'hidden'
      }}
    >
      <img src="/icons/google-logo.svg" alt="Google" width={24} height={24} style={{ pointerEvents: 'none' }} />
    </Box>
  );
};

const LoginBackground = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isMsalReady, setIsMsalReady] = useState(false);
  const errorTimeoutRef = useRef(null);
  const msalInitializedRef = useRef(new WeakSet());

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoading, error, isAuthenticated, tenantInfo, ssoLoading, googleSsoLoading } = useSelector(
    (state) => state.auth
  );

  const col1Images = [
    '/icons/login/login-bg-1.png',
    '/icons/login/login-bg-2.png',
    '/icons/login/login-bg-3.png'
  ];

  const col2Images = [
    '/icons/login/login-bg-4.png',
    '/icons/login/login-bg-5.png',
    '/icons/login/login-bg-6.png'
  ];

  const col3Images = [
    '/icons/login/login-bg-7.png',
    '/icons/login/login-bg-8.png',
    '/icons/login/login-bg-9.png'
  ];

  useEffect(() => {
    if (isAuthenticated) {
      const lastView = localStorage.getItem('lastSelectedView') || '/ticket-management';
      navigate(lastView);
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      errorTimeoutRef.current = setTimeout(() => {
        dispatch(clearError());
        errorTimeoutRef.current = null;
      }, 5000);
    }

    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, [error, dispatch]);

  const clearErrorOnInput = () => {
    if (error) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      dispatch(clearError());
    }
  };

  const validateEmail = (value) => REGEX.email.test(value);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    clearErrorOnInput();

    if (value && !validateEmail(value)) {
      setEmailError(t('auth.validEmailRequired'));
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError(t('auth.validEmailRequired'));
      return;
    }

    dispatch(loginUser({ email, password }));
  };

  const handleSSOLogin = async () => {
    if (!publicClientApplication || !isMsalReady) {
      console.error('MSAL not initialized');
      return;
    }

    try {
      const response = await publicClientApplication.loginPopup({
        scopes: ['User.Read'],
        prompt: 'select_account',
        redirectUri: getMsalPopupRedirectUri()
      });

      if (response) {
        const ssoLoginBody = {
          email: response.account.username,
          name: response.account.name,
          tenant_id: response.account.tenantId,
          id_token: response.idToken,
          access_token: response.accessToken
        };

        await dispatch(ssoLogin(ssoLoginBody));
      }
    } catch (ssoError) {
      console.error('SSO Login failed:', ssoError);
      dispatch(setError(getSSOErrorMessage(ssoError, t('auth.ssoLoginFailed'), t('auth.ssoLoginCancelled'))));
    }
  };

  const showGoogleSsoCancelled = () => {
    const message = t('auth.ssoLoginCancelled');
    console.warn('[Google SSO] Login cancelled by user');
    dispatch(setError(message));
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    console.info('[Google SSO] OAuth token response received');
    if (!tokenResponse?.access_token) {
      console.warn('[Google SSO] OAuth response missing access token');
      dispatch(setError(t('auth.googleSsoLoginFailed')));
      return;
    }

    if (!tenantInfo?.tenant_id) {
      console.warn('[Google SSO] Tenant info missing for Google SSO login');
      dispatch(setError(t('auth.googleSsoLoginFailed')));
      return;
    }

    console.info('[Google SSO] Dispatching backend SSO login');
    await dispatch(
      googleSsoLogin({
        access_token: tokenResponse.access_token,
        tenant_id: tenantInfo?.tenant_id
      })
    );
  };

  const handleGoogleError = (googleError) => {
    console.error('[Google SSO] Login failed:', googleError);
    dispatch(setError(t('auth.googleSsoLoginFailed')));
  };

  const handleGoogleCancel = (googleError) => {
    if (googleError?.type === 'popup_closed') {
      showGoogleSsoCancelled();
      return;
    }

    console.error('[Google SSO] Non-OAuth login error:', googleError);
    dispatch(setError(t('auth.googleSsoLoginFailed')));
  };

  useEffect(() => {
    if (!tenantInfo) {
      dispatch(fetchTenantInfo('wingmanpartners'));
    }
  }, [dispatch, tenantInfo]);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const publicClientApplication = getMsalInstance(tenantInfo);

  useEffect(() => {
    const initializeMSAL = async () => {
      if (!publicClientApplication) return;

      if (msalInitializedRef.current.has(publicClientApplication)) {
        setIsMsalReady(true);
        return;
      }

      setIsMsalReady(false);
      try {
        await publicClientApplication.initialize();
        msalInitializedRef.current.add(publicClientApplication);
        setIsMsalReady(true);
      } catch (msalError) {
        console.error('MSAL initialization failed:', msalError);
      }
    };

    initializeMSAL();
  }, [publicClientApplication]);

  const renderColumn = (images, animationClass) => (
    <div className="bg-column-wrapper">
      <div className={`bg-column ${animationClass}`}>
        {images.map((src, idx) => (
          <img key={`orig-${idx}`} src={src} alt="" className="bg-image" />
        ))}
        {images.map((src, idx) => (
          <img key={`dup-${idx}`} src={src} alt="" className="bg-image" />
        ))}
      </div>
    </div>
  );

  return (
    <div className="login-bg-container">
      {renderColumn(col1Images, 'scroll-down')}
      {renderColumn(col2Images, 'scroll-up')}
      {renderColumn(col3Images, 'scroll-down')}

      <div className="login-bg-overlay" />

      <div className="login-content-wrapper">
        <Box className="floating-badge badge-track">
          <img src="/icons/login/track-progress.svg" alt="" style={{ width: 24, height: 24 }} />
          <Typography sx={{ color: '#6FC812', fontWeight: 500, ml: 1 }}>Track Progress</Typography>
          <img className="pointer-icon" src="/icons/login/blinkArrow-green.png" alt="pointer" />
        </Box>

        <Box className="floating-badge badge-team">
          <img src="/icons/login/team-collaboration.svg" alt="" style={{ width: 24, height: 24 }} />
          <Typography sx={{ color: '#066E82', fontWeight: 500, ml: 1 }}>Team Collaboration</Typography>
          <img className="pointer-icon" src="/icons/login/blinkArrow-skyblue.png" alt="pointer" />
        </Box>

        <Box className="floating-badge badge-boost">
          <img src="/icons/login/boost-productivity.svg" alt="" style={{ width: 24, height: 24 }} />
          <Typography sx={{ color: '#ED7B1E', fontWeight: 500, ml: 1 }}>Boost Productivity</Typography>
          <img className="pointer-icon" src="/icons/login/blinkArrow-orange.png" alt="pointer" />
        </Box>

        <Box className="floating-badge badge-smart">
          <img src="/icons/login/smart-automation.svg" alt="" style={{ width: 24, height: 24 }} />
          <Typography sx={{ color: '#3B82F6', fontWeight: 500, ml: 1 }}>Smart Automation</Typography>
          <img className="pointer-icon" src="/icons/login/blinkArrow-blue.png" alt="pointer" />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 30 }}>
          <Paper elevation={12} className="login-modal-paper">
            <Box className="login-form-bg" aria-hidden="true">
              <img src="/icons/login/logoORKA.gif" alt="" className="login-form-bg-image" />
              <Box className="login-form-bg-overlay" />
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1, pt: 0 }}>
              <Box sx={{ textAlign: 'center', mb: 1.25 }}>
                <img
                  src="/icons/login/orkaflo.png"
                  alt="Orkaflo"
                  style={{
                    display: 'block',
                    margin: '0 auto 6px',
                    width: '182px',
                    height: '41px',
                    objectFit: 'contain'
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e', mb: 0.5 }}>
                  {t('auth.welcomeBack')}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#7f8c8d', fontSize: '1.3rem', mt: 0 }}>
                  {t('auth.signInToAccount')}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1.5 }}>
                <div className="custom-input-group">
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('auth.emailAddress')}
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    error={!!emailError}
                    helperText={emailError}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined className="input-icon-red" fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                    className="red-outlined-input"
                  />
                </div>

                <div className="custom-input-group">
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('auth.password')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearErrorOnInput();
                    }}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined className="input-icon-red" fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </div>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 3
                    }}
                  >
                    {error}
                  </Alert>
                )}

                  <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                    sx={{
                      mt: 1.5,
                      mb: 2.25,
                      py: 1.5,
                      bgcolor: '#1a237e',
                    borderRadius: 2,
                    fontSize: '16px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#121858' },
                    '&:disabled': {
                      bgcolor: '#b7c0df',
                      color: '#ffffff'
                    }
                  }}
                >
                  {isLoading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={dividerLine} />
                  <Typography sx={{ px: 2, color: COLORS.text.secondary, fontSize: '14px' }}>
                    {t('auth.or')}
                  </Typography>
                  <Box sx={dividerLine} />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleSSOLogin}
                    disabled={ssoLoading || !publicClientApplication || !isMsalReady}
                    title={ssoLoading ? t('auth.ssoSigningIn') : t('auth.signInWithSSO')}
                    sx={{
                      ...ssoIconButtonSx,
                      cursor: ssoLoading || !publicClientApplication || !isMsalReady ? 'not-allowed' : 'pointer',
                      opacity: ssoLoading || !publicClientApplication || !isMsalReady ? 0.5 : 1
                    }}
                  >
                    <img src="/icons/microsoft-logo.svg" alt="Microsoft" width={24} height={24} />
                  </Box>

                  {googleClientId && (
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <GoogleIconButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        onCancel={handleGoogleCancel}
                        loading={googleSsoLoading}
                        title={googleSsoLoading ? t('auth.googleSsoSigningIn') : t('auth.signInWithGoogle')}
                      />
                    </GoogleOAuthProvider>
                  )}
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                    sx={{
                      color: '#d32f2f',
                      textTransform: 'none',
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </div>
    </div>
  );
};

export default LoginBackground;
